import { useState, useEffect } from 'react';
import { Loader } from '@shared/ui/Progress';
import {
  useDeleteClaimMutation,
  useDeleteComposedMutation,
  useGetClaimDetailsQuery,
  useLinkClaimContractMutation,
  useUnlinkClaimContractMutation,
  useReanalyzeClaimMutation,
  useUpdateClaimStageMutation,
  useAddManualClaimRiskMutation,
  usePatchClaimRiskMutation,
  useDeleteClaimRiskMutation,
  useBulkAnnotateClaimRisksMutation,
  useComposeClaimResponseMutation,
  useListComposedResponsesQuery,
  useListStatutProjectsQuery,
  useSetClaimProjectMutation,
} from '@shared/api';
import { useAppSelector } from '@app/hooks';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { realtime } from '@shared/realtime/ws';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import { useAgentPermission } from '@entities/user';
import { ManualRiskModal } from './ManualRiskModal';
import { RiskClausePanel } from './RiskClausePanel';
import { RiskBulkActionBar } from './RiskBulkActionBar';
import { ExternalAuditModal } from './ExternalAuditModal';
import { CounterpartyCard } from './CounterpartyCard';
import { RedirectClaimModal } from './RedirectClaimModal';
import { ClaimHeaderSection } from './ClaimCard/ClaimHeaderSection';
import { ClaimRisksSection } from './ClaimCard/ClaimRisksSection';
import { ClaimPenaltySection } from './ClaimCard/ClaimPenaltySection';
import { ClaimDisputeSection } from './ClaimCard/ClaimDisputeSection';
import { ClaimDirectionHint } from './ClaimCard/ClaimDirectionHint';
import { categoryLabel } from './ClaimCard/styles';

interface Props {
  claimId: string;
  onClose: () => void;
}

export function ClaimCard({ claimId, onClose }: Props) {
  const { can_write } = useAgentPermission('statut');
  const accessToken = useAppSelector((s) => s.auth.accessToken);

  const { data, isLoading } = useGetClaimDetailsQuery(claimId, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const [deleteClaim, { isLoading: isDeleting }] = useDeleteClaimMutation();
  const [reanalyze, { isLoading: isReanalyzing }] = useReanalyzeClaimMutation();
  const [updateStage, { isLoading: isUpdatingStage }] = useUpdateClaimStageMutation();
  const [linkContract, { isLoading: isLinking }] = useLinkClaimContractMutation();
  const [unlinkContract, { isLoading: isUnlinking }] = useUnlinkClaimContractMutation();
  const [setClaimProject, { isLoading: isSettingProject }] = useSetClaimProjectMutation();

  const [addManualClaimRisk] = useAddManualClaimRiskMutation();
  const [patchClaimRisk] = usePatchClaimRiskMutation();
  const [deleteClaimRisk] = useDeleteClaimRiskMutation();
  const [bulkAnnotateClaim, { isLoading: isBulkAnnotating }] = useBulkAnnotateClaimRisksMutation();

  const [composeResponse, { isLoading: isComposing }] = useComposeClaimResponseMutation();
  const { data: composedData } = useListComposedResponsesQuery(claimId);
  const [deleteComposed] = useDeleteComposedMutation();
  const composedItems = composedData?.items ?? [];

  const { data: projectsData } = useListStatutProjectsQuery();
  const statutProjects = projectsData?.items ?? [];

  const [linkNotice, setLinkNotice] = useState<string | null>(null);
  const [composeMsg, setComposeMsg] = useState<{
    kind: 'error' | 'fallback' | 'ok';
    text: string;
  } | null>(null);
  const [composeFallback, setComposeFallback] = useState<string | null>(null);

  // Modals state
  const [auditOpen, setAuditOpen] = useState(false);
  const [redirectOpen, setRedirectOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [counterpartyCardId, setCounterpartyCardId] = useState<string | null>(null);
  const [manualRiskOpen, setManualRiskOpen] = useState(false);
  const [editingRiskId, setEditingRiskId] = useState<string | null>(null);
  const [deletingRiskId, setDeletingRiskId] = useState<string | null>(null);
  const [viewClauseRiskId, setViewClauseRiskId] = useState<string | null>(null);

  // Selection mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedRisks, setSelectedRisks] = useState<Set<string>>(new Set());

  const isProcessing =
    data?.claim.analysis_status === 'pending' || data?.claim.analysis_status === 'processing';

  // Listen to realtime WS events
  useEffect(() => {
    const unsub = realtime.subscribe((event) => {
      if (event.event === 'claim_response_composed' && event.claim_id === claimId) {
        if (event.fallback) {
          setComposeFallback(
            event.reason ?? 'Не удалось подобрать подходящий шаблон ответа в БЗ.',
          );
        } else {
          setComposeFallback(null);
        }
      }
    });
    return unsub;
  }, [claimId]);

  useEffect(() => {
    if (data?.claim.analysis_status === 'analyzed') setLinkNotice(null);
  }, [data?.claim.analysis_status]);

  function toggleRiskSelection(id: string) {
    setSelectedRisks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelectedRisks(new Set());
  }

  async function bulkDecide(decision: 'accepted' | 'disputed' | 'rejected') {
    if (selectedRisks.size === 0) return;
    await bulkAnnotateClaim({
      claimId,
      riskIds: Array.from(selectedRisks),
      decision,
    }).unwrap();
    exitSelectMode();
  }

  async function handleConfirmLink(contractId: string) {
    const wasLinked = !!data?.contract;
    try {
      await linkContract({ claimId, contractId }).unwrap();
      setLinkNotice(
        wasLinked
          ? 'Запущен повторный анализ с новым договором — карточка обновится автоматически.'
          : 'Договор связан, анализ перезапущен — карточка обновится автоматически.',
      );
    } catch {
      // Ошибка остается в UI
    }
  }

  async function handleConfirmUnlink() {
    try {
      await unlinkContract(claimId).unwrap();
      setLinkNotice(
        'Договор отвязан, анализ перезапущен без contract-блока — карточка обновится автоматически.',
      );
    } catch {
      // Ошибка остается в UI
    }
  }

  async function handleUpdateStage(stage: string) {
    await updateStage({ id: claimId, stage }).unwrap();
  }

  async function handleComposeResponse() {
    setComposeMsg(null);
    setComposeFallback(null);
    try {
      const res = await composeResponse(claimId).unwrap();
      if (res.status === 'fallback') {
        setComposeMsg({
          kind: 'fallback',
          text: `Шаблон не найден (${res.reason}). Используйте черновик ниже.`,
        });
      } else {
        setComposeMsg({
          kind: 'ok',
          text: 'Составляем DOCX. Обычно 30–60 секунд.',
        });
      }
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        'Не удалось запустить генерацию ответа. Попробуйте ещё раз.';
      setComposeMsg({ kind: 'error', text: msg });
    }
  }

  async function handleDownloadComposed(composedId: string) {
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
    const res = await downloadAuthorizedFile(
      `${base}/agents/statut/composed/${composedId}/download`,
      {
        accessToken,
        fallbackName: `Ответ-на-претензию-${claimId}.docx`,
      },
    );
    if (!res.ok) {
      setComposeMsg({
        kind: 'error',
        text: `Не удалось скачать DOCX (${res.error}). Попробуйте ещё раз.`,
      });
    }
  }

  async function handleDeleteComposed(composedId: string) {
    if (!window.confirm('Удалить этот сгенерированный DOCX-ответ? Действие необратимо.')) {
      return;
    }
    try {
      await deleteComposed(composedId).unwrap();
    } catch {
      setComposeMsg({
        kind: 'error',
        text: 'Не удалось удалить DOCX. Попробуйте ещё раз.',
      });
    }
  }

  async function handleDeleteClaim() {
    try {
      await deleteClaim(claimId).unwrap();
      setConfirmOpen(false);
      onClose();
    } catch {
      setConfirmOpen(false);
    }
  }

  if (isLoading || !data) {
    return <Loader />;
  }

  const claim = data.claim;
  const direction = claim.direction === 'outgoing' ? 'outgoing' : 'incoming';

  return (
    <>
      <ClaimHeaderSection
        claimId={claimId}
        claim={claim}
        contract={data.contract}
        relatedContractContext={data.related_contract_context}
        can_write={can_write}
        isProcessing={isProcessing}
        statutProjects={statutProjects}
        isSettingProject={isSettingProject}
        onSetClaimProject={(pid) => void setClaimProject({ claimId, project_id: pid })}
        onConfirmLink={handleConfirmLink}
        onConfirmUnlink={handleConfirmUnlink}
        isLinking={isLinking}
        isUnlinking={isUnlinking}
        linkNotice={linkNotice}
      />

      {data.direction_hint && !isProcessing && (
        <ClaimDirectionHint claimId={claimId} hint={data.direction_hint} can_write={can_write} />
      )}

      <ClaimRisksSection
        claimId={claimId}
        summary={claim.summary}
        analysisMeta={claim.analysis_meta as Record<string, unknown> | null}
        direction={direction}
        risks={data.risks ?? []}
        isProcessing={isProcessing}
        can_write={can_write}
        selectMode={selectMode}
        onEnterSelectMode={() => setSelectMode(true)}
        selectedRisks={selectedRisks}
        onToggleRiskSelection={toggleRiskSelection}
        onOpenManualRisk={() => setManualRiskOpen(true)}
        onViewClause={setViewClauseRiskId}
        onEditRisk={setEditingRiskId}
        onDeleteRisk={setDeletingRiskId}
        onComposeResponse={handleComposeResponse}
        isComposing={isComposing}
        composeMsg={composeMsg}
        composeFallback={composeFallback}
        onDismissComposeFallback={() => setComposeFallback(null)}
        composedItems={composedItems}
        onDownloadComposed={handleDownloadComposed}
        onDeleteComposed={handleDeleteComposed}
      />

      <ClaimPenaltySection amount={claim.amount} />

      <ClaimDisputeSection
        claimId={claimId}
        claimStage={claim.stage}
        claimAnalysisStatus={claim.analysis_status}
        hasOriginalFileHash={Boolean(claim.original_file_hash)}
        hasContractLinked={Boolean(claim.contract_id)}
        isIncoming={direction === 'incoming'}
        deadlines={data.deadlines}
        isProcessing={isProcessing}
        isReanalyzing={isReanalyzing}
        can_write={can_write}
        onOpenCounterparty={setCounterpartyCardId}
        onReanalyze={() => void reanalyze(claimId)}
        onOpenAudit={() => setAuditOpen(true)}
        onOpenRedirect={() => setRedirectOpen(true)}
        onUpdateStage={handleUpdateStage}
        isUpdatingStage={isUpdatingStage}
        onOpenConfirmDelete={() => setConfirmOpen(true)}
        isDeleting={isDeleting}
      />

      <ExternalAuditModal
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        scope="claim"
        entityId={claimId}
        entityLabel={claim.claim_type === 'lawsuit' ? 'Иск / суд. документ' : 'Претензия'}
      />
      <RedirectClaimModal
        open={redirectOpen}
        claimId={claimId}
        mainContractLinked={Boolean(claim.contract_id)}
        onClose={() => setRedirectOpen(false)}
        onSaved={() => {
          setRedirectOpen(false);
          setComposeMsg({
            kind: 'ok',
            text:
              'Запустили перевыставление. DOCX появится в блоке ' +
              '«Перевыставленные DOCX субподрядчику» через 30-60 секунд.',
          });
        }}
      />
      <ConfirmDialog
        open={confirmOpen}
        title={claim.claim_type === 'lawsuit' ? 'Удалить иск / суд. документ?' : 'Удалить претензию?'}
        warning={`Запись по контрагенту «${claim.counterparty}» будет полностью удалена вместе со всеми замечаниями LLM и связанными сроками. Действие необратимо.`}
        confirmText={claim.counterparty}
        confirmLabel="Введите название контрагента для подтверждения"
        confirmButtonLabel="Удалить безвозвратно"
        busyLabel="Удаляем…"
        isBusy={isDeleting}
        onConfirm={handleDeleteClaim}
        onCancel={() => setConfirmOpen(false)}
      />
      {counterpartyCardId && (
        <CounterpartyCard counterpartyId={counterpartyCardId} onClose={() => setCounterpartyCardId(null)} />
      )}
      <ManualRiskModal
        open={manualRiskOpen}
        mode="add"
        scope="claim"
        onClose={() => setManualRiskOpen(false)}
        onSubmit={async (v) => {
          await addManualClaimRisk({
            claimId,
            category: v.category,
            severity: v.severity,
            description: v.description,
            proposed_action: v.proposed,
          }).unwrap();
        }}
      />
      {editingRiskId &&
        (() => {
          const r = data?.risks.find((x) => x.id === editingRiskId);
          if (!r) return null;
          return (
            <ManualRiskModal
              open={true}
              mode="edit"
              scope="claim"
              initial={{
                category: r.category,
                severity: r.severity as 'critical' | 'major' | 'minor',
                description: r.description,
                proposed: r.proposed_action ?? '',
              }}
              onClose={() => setEditingRiskId(null)}
              onSubmit={async (v) => {
                await patchClaimRisk({
                  claimId,
                  riskId: r.id,
                  category: v.category,
                  severity: v.severity,
                  description: v.description,
                  proposed_action: v.proposed,
                }).unwrap();
              }}
            />
          );
        })()}
      <ConfirmDialog
        open={!!deletingRiskId}
        title="Удалить риск?"
        warning="Риск будет удалён из претензии/иска и из памяти агента. Действие необратимо."
        confirmText="удалить"
        confirmLabel="Введите «удалить» для подтверждения"
        confirmButtonLabel="Удалить риск"
        busyLabel="Удаляем…"
        isBusy={false}
        onConfirm={async () => {
          if (!deletingRiskId) return;
          await deleteClaimRisk({ claimId, riskId: deletingRiskId }).unwrap();
          setDeletingRiskId(null);
        }}
        onCancel={() => setDeletingRiskId(null)}
      />
      {viewClauseRiskId &&
        (() => {
          const r = data?.risks.find((x) => x.id === viewClauseRiskId);
          if (!r) return null;
          return (
            <RiskClausePanel
              open={true}
              title={categoryLabel(direction, r.category)}
              severity={r.severity}
              clauseQuote={r.clause_quote}
              description={r.description}
              proposed={r.proposed_action}
              evidence={r.evidence_used}
              onClose={() => setViewClauseRiskId(null)}
            />
          );
        })()}
      {selectMode && (
        <RiskBulkActionBar
          selectedCount={selectedRisks.size}
          totalCount={data.risks?.length ?? 0}
          allSelected={(data.risks?.length ?? 0) > 0 && selectedRisks.size === (data.risks?.length ?? 0)}
          onToggleAll={() => {
            const all = data.risks ?? [];
            if (selectedRisks.size === all.length) {
              setSelectedRisks(new Set());
            } else {
              setSelectedRisks(new Set(all.map((r) => r.id)));
            }
          }}
          onCancel={exitSelectMode}
          onDecide={bulkDecide}
          busy={isBulkAnnotating}
        />
      )}
    </>
  );
}

export { ClaimHeaderSection } from './ClaimCard/ClaimHeaderSection';
export { ClaimRisksSection } from './ClaimCard/ClaimRisksSection';
export { ClaimPenaltySection } from './ClaimCard/ClaimPenaltySection';
export { ClaimDisputeSection } from './ClaimCard/ClaimDisputeSection';
